import { DestroyRef, inject, Injectable, Injector, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
    AiEditChangeEvent,
    AiEditConflictEvent,
    AiEditStatusEvent,
    ClientController,
    CoverTemplateController,
    DocGenPdfStatusEnum,
    DocumentConfigPartial,
    DocumentConfigScopeEnum,
    DocumentController,
    DocumentCoverController,
    DocumentFooterController,
    DocumentGenericField,
    DocumentHeaderController,
    DocumentItemPartial,
    DocumentItemTypeEnum,
    DocumentResponsiblePartial,
    DocumentSessionChecklistPartial,
    DocumentSessionPartial,
    DocumentStatusEnum,
    SharedWithPartial,
    SignedByPartial,
    UserEntity,
} from '@domain';
import { environment } from '@environments';
import { TranslateService } from '@ngx-translate/core';
import { RaToastService } from '@shared/components';
import {
    catchError,
    EMPTY,
    exhaustMap,
    interval,
    Observable,
    of,
    Subject,
    switchMap,
    tap,
    throwError,
} from 'rxjs';
import { DocumentFieldsController } from 'src/app/domain/controllers/document/document-fields.controller';
import { DocumentSectionController } from 'src/app/domain/controllers/document/document-section.controller';
import { DocumentEntity } from 'src/app/domain/entities/document.entity';
import { CoverTemplateEntity } from 'src/app/domain/entities/partials/cover-template/cover-template.entity';
import { DocumentCoverPartial } from 'src/app/domain/entities/partials/documents/document-cover.partial';
import { DocumentSessionFocusedPartial } from 'src/app/domain/entities/partials/documents/document-session.partial';
import { DocumentSessionTypeEnum } from 'src/app/domain/enums/documents/document-session-type.enum';
import { DocumentPdfGenerationService } from 'src/app/shared/services/document-pdf-generation.service';
import { DigitalOceanStorage } from 'src/app/shared/utils/digital-ocean/digital-ocean-storage';
import { PusherService } from 'src/app/shared/utils/pusher/pusher.service';
import { AiEditStore } from './ai-edit.store';
import { UtilService } from 'src/app/shared/utils/utils.service';
import { EditDataService } from './_edit-data.service';
import { FieldRemovalService } from './field-removal.service';
import { MenuOptionsService } from './menu-options.service';
import { OriginalTextService } from './original-text.service';

@Injectable({
    providedIn: 'root',
})
export class EditService {
    public loading = signal(false);
    /** Chave i18n da mensagem de loading (traduzida no template/diretiva). */
    public loadingMessageKey = signal<string | null>(null);
    /** `true` durante a clonagem do documento — dispara o overlay full-screen (mesmo padrão da criação). */
    public cloningDocument = signal(false);
    /** `true` durante o move do documento pra outro order — mesmo overlay full-screen. */
    public movingDocument = signal(false);
    /**
     * `true` durante a correção ortográfica com IA (documento inteiro ou seção).
     * Dispara o overlay full-screen bloqueante para impedir o usuário de sair da
     * tela no meio — trocar de tela durante a correção gerava um "erro de servidor"
     * mesmo com o conteúdo sendo gerado.
     */
    public correctingAllTexts = signal(false);
    /**
     * Chave i18n da ação de IA disparada por um card (correção ortográfica, usar como
     * prompt, descrição automática). Enquanto preenchida, dispara o overlay full-screen
     * bloqueante — mesmo padrão de "corrigir todos os textos": a IA reescreve o conteúdo
     * do campo e sair da tela no meio quebra o fluxo.
     */
    public fieldIaActionKey = signal<string | null>(null);

    /**
     * Chave i18n de uma ação de mídia de card (trocar/editar/remover imagem).
     * Mesmo overlay bloqueante do [fieldIaActionKey]: são uploads que não podem
     * ser interrompidos no meio. O `loading` sozinho não serve — ele só troca a
     * lista de itens por skeletons quando ela está vazia e acende um indicador
     * discreto no header, então a tela parecia travada durante o upload.
     */
    public fieldMediaActionKey = signal<string | null>(null);

    /**
     * Emite quando ESTE usuário acabou de concluir a última seção (checklist 100%).
     * A `ChecklistBar` escuta para perguntar se quer avisar os administradores.
     * Só dispara no cliente que executou a marcação (não nos que recebem via realtime).
     */
    public checklistCompleted$ = new Subject<void>();

    // * [Injects]
    private _router = inject(Router);
    private _utilService = inject(UtilService);
    private _toastService = inject(RaToastService);
    private _injector = inject(Injector);
    private _pusherService = inject(PusherService);
    private _editDataService = inject(EditDataService);
    private _clientController = inject(ClientController);
    private _documentController = inject(DocumentController);
    private _documentFooterController = inject(DocumentFooterController);
    private _documentHeaderController = inject(DocumentHeaderController);
    private _documentCoverController = inject(DocumentCoverController);
    private _coverTemplateController = inject(CoverTemplateController);
    private _digitalOcean = inject(DigitalOceanStorage);
    private _documentFieldsController = inject(DocumentFieldsController);
    private _documentSessionController = inject(DocumentSectionController);
    private _pdfGenerationService = inject(DocumentPdfGenerationService);
    private _menuOptionsService = inject(MenuOptionsService);
    private _fieldRemovalService = inject(FieldRemovalService);
    private _originalTextService = inject(OriginalTextService);
    private _aiEditStore = inject(AiEditStore);

    private _t(key: string, params?: Record<string, string | number>): string {
        return this._injector.get(TranslateService).instant(key, params);
    }

    /**
     * Busca o documento pelo ID e inicializa o serviço de edição
     * @param documentId ID do documento
     */
    public initialize(documentId: string, _destroyRef: DestroyRef): void {
        this.initLoading('document.edit.service.loadingDocument');

        // Limpa o documento em cache
        this._editDataService.clearDocument();

        // Inscreve o realtime ANTES de buscar o documento, para não perder
        // eventos emitidos durante o carregamento (os handlers ignoram
        // eventos enquanto o documento ainda não foi carregado, e o getById
        // já retorna o estado que inclui essas mudanças)
        this.initializePusher(documentId, _destroyRef);

        this.loading.set(true);
        this._documentController
            .getById(documentId)
            .pipe(
                tap(document => {
                    this._editDataService.setDocument(document);
                    // Reabre o progresso se uma geração já estava em curso (outra tela/aba).
                    this._adoptPdfGenerationIfInProgress(document.pdfStatus);
                }),
                switchMap(() =>
                    this._clientController.getByDocumentId(documentId).pipe(
                        tap(client => this._editDataService.setClient(client)),
                        catchError(() => of(null)),
                        switchMap(() => this._documentController.addOnlineUser(documentId)),
                        // Aplica apenas os usuários online: substituir o documento
                        // inteiro aqui poderia sobrescrever eventos realtime já aplicados
                        tap(updatedDocument =>
                            this._editDataService.documentOnlineUsersSet(
                                updatedDocument?.onlineUsers ?? []
                            )
                        )
                    )
                ),
                takeUntilDestroyed(_destroyRef)
            )
            .subscribe({
                next: () => {
                    // Mantém o usuário online enquanto a tela estiver aberta
                    this._initializeOnlineUserRefresh(documentId, _destroyRef);
                    this._initializeSessionFocusRefresh(documentId, _destroyRef);

                    // Verifica se tem uma seção para abrir o menu de opções
                    this._checkCountSessionToOpenMenu();

                    // Finaliza o loading
                    this.stopLoading();
                },
                error: error => {
                    this.stopLoading();
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.loadDocumentError')
                    );

                    this._router.navigate(['/platform']);
                },
            });
    }

    /**
     * Verifica se tem uma seção para abrir o menu de opções
     */
    private _checkCountSessionToOpenMenu(): void {
        const document = this._editDataService.document();

        // Se não tem sessões, abre o menu de opções
        if (!document?.sessions || document.sessions.length == 0) {
            this._menuOptionsService.showMenuOptions();
            return;
        }

        // Se tem sessões, verifica se tem uma seção para abrir o menu de opções
        if (document.sessions.length == 1 && document.sessions[0].items.length == 0) {
            this._menuOptionsService.showMenuOptions();
            return;
        }
    }

    /**
     * Salva as configurações do documento (showCover, showSummary, fontFamily)
     * @param config Configurações do documento
     * @returns Observable com o documento atualizado
     */
    public saveDocumentConfig(config: DocumentConfigPartial, showSuccessToast = true) {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return EMPTY;

        return this._documentController.updateConfig(documentId, config, showSuccessToast).pipe(
            // Só a `config` volta para o estado local: este é um auto-save com
            // debounce e substituir o documento inteiro pela resposta descartava
            // edições feitas nos cards enquanto o PATCH estava em voo.
            tap(doc => this._editDataService.applyDocumentConfig(doc?.config ?? config))
        );
    }

    /**
     * Aplica o grid de imagens escolhido a TODOS os grupos de imagens do documento.
     * Atualiza o estado local (preservando a seção atual) no sucesso.
     */
    public applyGridToAllGroups(groupImageGrid: number) {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return EMPTY;

        return this._documentController
            .applyGridToAllGroups(documentId, groupImageGrid)
            .pipe(tap(() => this._editDataService.documentGroupImageGridApplied(groupImageGrid)));
    }

    /**
     * Promove a configuração já gravada no documento a padrão, no alcance escolhido
     * pelo usuário ao salvar. O escopo "só este documento" não chega aqui: nada há a
     * propagar, o auto-save já gravou.
     */
    public saveConfigScope(scope: DocumentConfigScopeEnum) {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return EMPTY;

        return this._documentController.saveConfigScope(documentId, scope);
    }

    /**
     * Garante que o documento tenha um footer. Se ainda não tiver, semeia o
     * default no backend (espelha o `getDocumentFooter` do app mobile).
     * @returns Observable com o documento (semeado ou já existente)
     */
    public seedFooterIfEmpty() {
        const document = this._editDataService.document();
        const documentId = document?._id;
        if (!documentId) return EMPTY;

        // Já tem footer → não mexe
        if (document.footer?.length) return of(document);

        return this._documentFooterController
            .setDefault(documentId)
            .pipe(tap(doc => this._editDataService.setDocument(doc)));
    }

    /**
     * Salva o conteúdo do footer.
     * @param footer Lista de campos do footer
     * @returns Observable com o documento atualizado
     */
    public saveFooter(footer: DocumentGenericField[]) {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return EMPTY;

        return this._documentFooterController
            .updateFooterData(documentId, footer)
            .pipe(tap(doc => this._editDataService.setDocument(doc)));
    }

    /**
     * Garante que o documento tenha um header (semeia o default se não tiver).
     * @returns Observable com o documento (semeado ou já existente)
     */
    public seedHeaderIfEmpty() {
        const document = this._editDataService.document();
        const documentId = document?._id;
        if (!documentId) return EMPTY;

        if (document.header?.length) return of(document);

        return this._documentHeaderController
            .setDefault(documentId)
            .pipe(tap(doc => this._editDataService.setDocument(doc)));
    }

    /**
     * Salva o conteúdo do header.
     * @param header Lista de campos do header
     */
    public saveHeader(header: DocumentGenericField[]) {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return EMPTY;

        return this._documentHeaderController
            .updateHeaderData(documentId, header)
            .pipe(tap(doc => this._editDataService.setDocument(doc)));
    }

    /**
     * Faz upload da imagem (logo) do header para a URL assinada e devolve a URL final.
     * @param file Arquivo de imagem
     */
    public uploadHeaderLogo(file: File) {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return EMPTY;

        return this._documentHeaderController
            .generateUploadUrl(documentId, file.type)
            .pipe(switchMap(signedUrl => this._digitalOcean.uploadToSignedUrl(file, signedUrl)));
    }

    // * [Cover]

    /** Busca o template de capa atual já preenchido com os dados do documento. */
    public getCoverTemplate(): Observable<CoverTemplateEntity> {
        return this._coverTemplateController.getById(this._editDataService.getDocumentId());
    }

    /** Lista todos os templates de capa disponíveis. */
    public getAllCoverTemplates(): Observable<CoverTemplateEntity[]> {
        return this._coverTemplateController.getAll(this._editDataService.getDocumentId());
    }

    /** Troca o template da capa e retorna o novo template preenchido. */
    public changeCoverTemplate(templateId: string): Observable<CoverTemplateEntity> {
        const documentId = this._editDataService.getDocumentId();
        return this._documentCoverController.changeCoverTemplate(documentId, templateId).pipe(
            tap(cover => this._editDataService.documentCoverUpdated(cover)),
            switchMap(() => this._coverTemplateController.getById(documentId))
        );
    }

    /**
     * Salva o conteúdo da capa.
     * @param cover Capa do documento
     */
    public saveCover(cover: DocumentCoverPartial) {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return EMPTY;

        return this._documentCoverController
            .updateCoverData(documentId, cover)
            .pipe(tap(doc => this._editDataService.setDocument(doc)));
    }

    /**
     * Faz upload de uma imagem da capa para a URL assinada e devolve a URL final.
     * @param file Arquivo de imagem
     */
    public uploadCoverImage(file: File) {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return EMPTY;

        return this._documentCoverController
            .generateUploadUrl(documentId, file.type)
            .pipe(switchMap(signedUrl => this._digitalOcean.uploadToSignedUrl(file, signedUrl)));
    }

    /**
     * Salva os signatários do documento
     * @param signers Lista de pessoas que devem assinar
     * @returns Observable com o documento atualizado
     */
    public saveDocumentSigners(signers: SignedByPartial[]) {
        const document = this._editDataService.document();
        if (!document?._id) return EMPTY;

        return this._documentController
            .update({
                ...document,
                signedBy: signers,
            })
            .pipe(tap(doc => this._editDataService.setDocument(doc)));
    }

    /**
     * Liga/desliga o modo privado do documento (toggle público/privado das
     * configurações). Só vale para NORMAL e QUOTE; o backend popula `sharedWith`
     * ao tornar privado. Atualiza o documento local com o retorno.
     * @param isPrivate `true` = privado; `false` = público
     */
    public updateVisibility(isPrivate: boolean) {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return EMPTY;

        return this._documentController
            .updateVisibility(documentId, isPrivate)
            .pipe(tap(doc => this._editDataService.setDocument(doc)));
    }

    /**
     * Atualiza a lista de "Quem pode acessar" (sharedWith) do documento.
     * @param sharedWith Pessoas que podem acessar
     */
    public updateSharedWith(sharedWith: SharedWithPartial[]) {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return EMPTY;

        return this._documentController
            .updateSharedWith(documentId, sharedWith)
            .pipe(tap(doc => this._editDataService.setDocument(doc)));
    }

    // * [Checklist / Responsibles]

    /**
     * Liga/desliga o modo checklist (otimista, com rollback em caso de erro).
     */
    public toggleChecklistMode(checklistMode: boolean): void {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return;

        const previous = !checklistMode;
        this._editDataService.documentChecklistModeUpdated(checklistMode);

        this._documentController.toggleChecklistMode(documentId, checklistMode).subscribe({
            next: doc => this._editDataService.setDocument(doc),
            error: () => this._editDataService.documentChecklistModeUpdated(previous),
        });
    }

    /**
     * Marca/desmarca uma seção como concluída (só responsáveis). Otimista com rollback;
     * a resposta do servidor preenche autor/data.
     */
    public markSectionCompleted(sessionId: string, done: boolean): void {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return;

        const previous = this._editDataService
            .document()
            ?.sessions.find(s => s._id === sessionId)?.checklist;

        // Otimista: preserva os metadados (doneBy*/doneAt) para não "piscar" enquanto
        // a resposta do servidor (autor/data definitivos) não chega.
        this._editDataService.sessionChecklistUpdated(sessionId, { ...(previous ?? {}), done });

        this._documentSessionController.setSessionCompleted(documentId, sessionId, done).subscribe({
            next: doc => {
                this._editDataService.setDocument(doc);
                // Concluiu a última seção → avisa a ChecklistBar para perguntar sobre notificar admins
                if (done && this._isChecklistComplete(doc)) this.checklistCompleted$.next();
            },
            error: () =>
                this._editDataService.sessionChecklistUpdated(
                    sessionId,
                    previous ?? { done: false }
                ),
        });
    }

    /**
     * Marca/desmarca uma PASTA (CHECKLIST_FOLDER) e deriva a conclusão da seção.
     * Otimista com rollback; mescla a resposta do servidor (carimbo "concluída
     * por X") sem substituir o documento — assim a `currentSession` da tela de
     * itens não fica obsoleta e marcações seguintes refletem.
     */
    public markFolderCompleted(sessionId: string, folderId: string, done: boolean): void {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return;

        const section = this._editDataService.document()?.sessions.find(s => s._id === sessionId);
        const folder = (section?.items ?? []).find(i => i._id === folderId);
        const previousFolder = folder?.checklist;
        const previousSection = section?.checklist;

        const optimisticFolder: DocumentSessionChecklistPartial = done
            ? { ...(previousFolder ?? {}), done: true }
            : { done: false };
        const optimisticSection = this._deriveSectionFromFolders(
            section,
            folderId,
            optimisticFolder
        );

        this._editDataService.folderChecklistUpdated(
            sessionId,
            folderId,
            optimisticFolder,
            optimisticSection
        );

        this._documentSessionController
            .setSessionCompleted(documentId, sessionId, done, folderId)
            .subscribe({
                next: doc => {
                    const srvSection = doc?.sessions?.find(s => s._id === sessionId);
                    const srvFolder = (srvSection?.items ?? []).find(i => i._id === folderId);
                    this._editDataService.folderChecklistUpdated(
                        sessionId,
                        folderId,
                        srvFolder?.checklist ?? optimisticFolder,
                        srvSection?.checklist ?? optimisticSection
                    );
                    if (done && this._isChecklistComplete(doc)) this.checklistCompleted$.next();
                },
                error: () =>
                    this._editDataService.folderChecklistUpdated(
                        sessionId,
                        folderId,
                        previousFolder ?? { done: false },
                        previousSection ?? { done: false }
                    ),
            });
    }

    /**
     * Renomeia um campo pelo id (otimista, com rollback). É o título da caixa de
     * destaque: a seção dona vem do documento inteiro, porque o card pode estar
     * numa seção que não é a aberta (ex.: ação vinda da prévia).
     */
    public renameField(fieldId: string, name: string): void {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return;

        const documentId = this._editDataService.getDocumentId();
        const owner = this._editDataService.findFieldAnywhere(fieldId);
        const sessionId = owner?.sessionId ?? this._editDataService.getCurrentSessionId();
        if (!documentId || !sessionId) return;

        const previous = owner?.field.name ?? '';
        this._editDataService.folderRenamed(sessionId, fieldId, name);

        this._documentSessionController
            .updateFieldName(documentId, sessionId, fieldId, name)
            .subscribe({
                error: () => this._editDataService.folderRenamed(sessionId, fieldId, previous),
            });
    }

    /** Renomeia uma pasta de checklist (otimista, com rollback). */
    public renameChecklistFolder(sessionId: string, folderId: string, name: string): void {
        // Documento enviado para assinatura (PUBLISHED/SIGNED) não pode ser editado.
        if (this._editDataService.documentIsBlocked()) return;

        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return;

        const previous = this._editDataService
            .document()
            ?.sessions.find(s => s._id === sessionId)
            ?.items?.find(i => i._id === folderId)?.name;

        this._editDataService.folderRenamed(sessionId, folderId, name);

        this._documentSessionController
            .updateFieldName(documentId, sessionId, folderId, name)
            .subscribe({
                error: () =>
                    this._editDataService.folderRenamed(sessionId, folderId, previous ?? ''),
            });
    }

    /** Deriva o check da seção a partir das pastas (≥1 pasta e todas concluídas). */
    private _deriveSectionFromFolders(
        section: DocumentSessionPartial,
        folderId: string,
        folderChecklist: DocumentSessionChecklistPartial
    ): DocumentSessionChecklistPartial {
        const folders = (section?.items ?? [])
            .filter(i => i.type === DocumentItemTypeEnum.CHECKLIST_FOLDER)
            .map(i => (i._id === folderId ? { ...i, checklist: folderChecklist } : i));
        const allDone = folders.length > 0 && folders.every(f => f.checklist?.done);
        return { done: allDone };
    }

    /** `true` se todas as seções não-assinatura estão concluídas (e há ao menos uma). */
    private _isChecklistComplete(doc: DocumentEntity): boolean {
        const list = (doc?.sessions ?? []).filter(
            s => s.type !== DocumentSessionTypeEnum.SIGNATURE
        );
        return list.length > 0 && list.every(s => s.checklist?.done);
    }

    /**
     * Reseta o checklist do documento (desmarca todas as seções).
     */
    public resetChecklist(): void {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return;

        // Otimista: limpa local antes da resposta; em caso de erro, restaura o snapshot.
        const previousDoc = this._editDataService.document();
        this._editDataService.documentChecklistReset();

        this._documentSessionController.resetChecklist(documentId).subscribe({
            next: doc => this._editDataService.setDocument(doc),
            error: () => {
                if (previousDoc) this._editDataService.setDocument(previousDoc);
            },
        });
    }

    /**
     * Avisa os owners/admins que o checklist foi concluído (só responsáveis).
     */
    public notifyChecklistCompleted(): void {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return;

        // O toast de erro já é tratado no controller (handleError); aqui só evitamos
        // o erro propagar para o handler global de erros do Angular.
        this._documentController
            .notifyChecklistCompleted(documentId)
            .subscribe({ error: () => {} });
    }

    /**
     * Atualiza a descrição de uma seção (otimista; resposta confirma).
     */
    public updateSessionDescription(sessionId: string, description: string): void {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return;

        const previousDescription =
            this._editDataService.document()?.sessions.find(s => s._id === sessionId)
                ?.description ?? '';

        this._editDataService.sessionDescriptionUpdated(description, sessionId);

        this._documentSessionController
            .updateSectionDescription(documentId, sessionId, description)
            .subscribe({
                next: doc => this._editDataService.setDocument(doc),
                error: () =>
                    this._editDataService.sessionDescriptionUpdated(previousDescription, sessionId),
            });
    }

    /**
     * Adiciona um responsável ao documento (criador/owner/admin).
     */
    public addResponsible(userId: string) {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return EMPTY;

        return this._documentController
            .addResponsible(documentId, userId)
            .pipe(tap(doc => this._editDataService.setDocument(doc)));
    }

    /**
     * Remove um responsável do documento (criador/owner/admin).
     */
    public removeResponsible(userId: string) {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return EMPTY;

        return this._documentController
            .removeResponsible(documentId, userId)
            .pipe(tap(doc => this._editDataService.setDocument(doc)));
    }

    /**
     * Salva o novo título do documento
     * @param title Novo título do documento
     */
    public saveNewTitle(title: string): void {
        //  Se o título for vazio, cancela a edição
        if (!title) {
            this._toastService.error({
                message: this._t('document.edit.service.emptyTitleError'),
            });
            return;
        }

        // Busca o documento
        const document = this._editDataService.document();

        // Se o título for o mesmo, cancela a edição
        if (title == document?.title) return;

        this._editDataService.setDocument({ ...document, title });

        // Salva o documento. Já aplicado acima: a pílula "Salvando" basta, sem o loading do
        // editor (ele desabilita e apaga a folha inteira durante o ida-e-volta).
        this._editDataService.beginSave();
        this._documentController.updateTitle(document._id, title).subscribe({
            complete: () => this._editDataService.endSave(),
            error: () => this._editDataService.endSave(false),
        });
    }

    /**
     * Realiza a correção ortográfica dos campos de texto com IA. Sem `sectionId`
     * corrige o documento inteiro; com `sectionId` corrige apenas a seção.
     *
     * Usa o overlay full-screen bloqueante (mesmo padrão do clone/move) em vez do
     * loading pequeno do header: enquanto a IA corrige, o usuário não deve trocar de
     * tela — se sair no meio, a requisição podia falhar com "erro de servidor".
     */
    public typoAllTextsIa(sectionId?: string): void {
        this.correctingAllTexts.set(true);

        const documentId = this._editDataService.getDocumentId();

        // A correção lê o documento do SERVIDOR: confirma uma remoção pendente
        // de desfazer antes, senão a IA corrige (e cobra) campo já removido.
        this._fieldRemovalService
            .flush()
            .pipe(
                switchMap(() => {
                    // Guarda o texto atual de cada campo como "texto original"
                    // antes da correção em massa — a correção chega via Pusher
                    // campo a campo e cada card passa a oferecer "Voltar ao
                    // texto original".
                    const snapshotFieldIds = this._saveOriginalsBeforeBulkCorrection(
                        documentId,
                        sectionId
                    );

                    return this._documentFieldsController
                        .allTextsCorrectionIa(documentId, sectionId)
                        .pipe(
                            catchError(error => {
                                // A correção não aconteceu: descarta os backups
                                // criados agora (os que já existiam permanecem).
                                this._originalTextService.clearMany(documentId, snapshotFieldIds);
                                return throwError(() => error);
                            })
                        );
                })
            )
            .subscribe({
                next: () => this.correctingAllTexts.set(false),
                error: () => this.correctingAllTexts.set(false),
            });
    }

    /**
     * Snapshot pré-correção em massa: `value` de TEXT/TITLE/SUBTITLE e
     * `description` das mídias. O primeiro backup de cada campo vence, então
     * rodar de novo não sobrescreve o texto do usuário.
     * @returns fieldIds cujo backup foi criado agora (para rollback)
     */
    private _saveOriginalsBeforeBulkCorrection(documentId: string, sectionId?: string): string[] {
        const sessions = this._editDataService.document()?.sessions ?? [];
        const target = sectionId ? sessions.filter(section => section._id === sectionId) : sessions;

        const texts: Record<string, string> = {};
        for (const section of target) {
            for (const item of section.items ?? []) {
                if (!item?._id) continue;

                const isTextType =
                    item.type === DocumentItemTypeEnum.TEXT ||
                    item.type === DocumentItemTypeEnum.TITLE ||
                    item.type === DocumentItemTypeEnum.SUBTITLE;
                const text = isTextType ? item.value : item.description;
                if (typeof text === 'string' && text.length > 0) texts[item._id] = text;
            }
        }

        return this._originalTextService.saveMany(documentId, texts);
    }

    /**
     * Remove o documento
     */
    public removeDocument(): void {
        this._documentController.remove(this._editDataService.getDocumentId()).subscribe({
            next: () => {
                this._editDataService.documentDeleted.set(true);
                this._router.navigate(['/platform']);
            },
        });
    }

    /**
     * Duplica o documento (JSON + mídias) dentro do mesmo order e navega para a cópia.
     * Usa o mesmo overlay full-screen do fluxo de criação de documento (bloqueia a tela
     * inteira em vez do loading pequeno do header, já que a navegação troca de rota).
     */
    public cloneDocument(): void {
        const document = this._editDataService.document();
        if (!document) return;

        this.cloningDocument.set(true);

        // A cópia é feita a partir do documento no SERVIDOR: confirma uma
        // remoção pendente antes, senão o clone nasce com o campo removido.
        this._fieldRemovalService
            .flush()
            .pipe(switchMap(() => this._documentController.clone(document._id, document.orderId)))
            .subscribe({
                next: clonedDocument => {
                    // `document/:id/edit` é uma única rota — navegar pra outro :id nela faz
                    // o Angular reaproveitar o EditPage (não reroda ngOnInit/initialize()),
                    // então a tela ficaria travada no documento antigo. Passa por uma rota
                    // neutra antes (sem alterar a URL visível) pra forçar destruir/recriar
                    // a tela do editor com o documento clonado.
                    this._router
                        .navigateByUrl('/platform', { skipLocationChange: true })
                        .then(() => {
                            this.cloningDocument.set(false);
                            this._documentController.navigateToDocument(clonedDocument, true, {
                                cloned: true,
                            });
                        });
                },
                error: () => this.cloningDocument.set(false),
            });
    }

    /**
     * Move o documento (JSON + mídias) para outro order da mesma empresa e
     * navega para ele. Mesmo overlay full-screen do clone; sem tour guiado
     * (o título não muda, então não há nada novo a explicar).
     */
    public moveDocument(orderDestinationId: string): void {
        const document = this._editDataService.document();
        if (!document) return;

        this.movingDocument.set(true);

        // Move leva o documento do SERVIDOR e marca o original para exclusão:
        // a remoção pendente precisa chegar antes, senão ela seria enviada para
        // um documento que já não existe (e o movido ficaria com o campo).
        this._fieldRemovalService
            .flush()
            .pipe(switchMap(() => this._documentController.move(document._id, orderDestinationId)))
            .subscribe({
                next: movedDocument => {
                    // A API já marcou o documento ORIGINAL pra exclusão (soft delete) —
                    // sinaliza isso pro EditPage não tentar "remover usuário online" dele
                    // no ngOnDestroy (roda já já, no hop pra rota neutra abaixo): esse
                    // documento não existe mais pra API, e a chamada voltaria com erro
                    // "Documento não encontrado" mesmo com o move tendo dado certo.
                    this._editDataService.documentDeleted.set(true);

                    // Mesmo motivo do clone: document/:id/edit é uma única rota, então
                    // força destruir/recriar a tela passando por uma rota neutra antes.
                    this._router
                        .navigateByUrl('/platform', { skipLocationChange: true })
                        .then(() => {
                            this.movingDocument.set(false);
                            this._documentController.navigateToDocument(movedDocument, true);
                        });
                },
                error: () => this.movingDocument.set(false),
            });
    }

    /**
     * Compartilhar link: abre a prévia pública em outra aba e já copia a URL
     * para a área de transferência (o compartilhamento é o motivo de abrir).
     */
    public shareLink(): void {
        const previewPageBase = environment.PLATFORM_BASE;
        const documentId = this._editDataService.getDocumentId();
        const previewPage = `${previewPageBase}document/${documentId}/preview`;

        // Copia junto com a abertura: é o mesmo gesto do usuário, então a
        // permissão de clipboard vale aqui (não esperar o flush, que é async).
        navigator.clipboard
            ?.writeText(previewPage)
            .then(() =>
                this._toastService.success({
                    message: this._t('document.edit.service.linkCopied'),
                })
            )
            .catch(() => null);

        // A prévia é renderizada a partir do documento do servidor: abre a aba
        // já (senão o popup blocker barra) e confirma a remoção pendente antes,
        // para o campo removido não reaparecer lá.
        const previewTab = window.open('', '_blank');
        this._fieldRemovalService.flush().subscribe({
            next: () => {
                if (previewTab) previewTab.location.href = previewPage;
                else window.open(previewPage, '_blank');
            },
        });
    }

    /**
     * Adota uma geração de PDF já em andamento (documento aberto com status
     * PENDING/CREATING) — o `DocumentPdfGenerationService` reabre o toast de progresso
     * e segue escutando o canal até concluir.
     */
    private _adoptPdfGenerationIfInProgress(pdfStatus?: DocGenPdfStatusEnum): void {
        if (
            pdfStatus === DocGenPdfStatusEnum.PENDING ||
            pdfStatus === DocGenPdfStatusEnum.CREATING
        ) {
            this._pdfGenerationService.adopt(
                this._editDataService.getDocumentId(),
                this._editDataService.document()?.title
            );
        }
    }

    public initLoading(messageKey: string): void {
        this.loading.set(true);
        this.loadingMessageKey.set(messageKey);
    }

    public stopLoading(): void {
        this.loading.set(false);
        this.loadingMessageKey.set(null);
    }

    /**
     * Mantém o usuário online no documento enquanto a tela de edição estiver ativa
     */
    private _initializeOnlineUserRefresh(documentId: string, _destroyRef: DestroyRef): void {
        interval(10000)
            .pipe(
                exhaustMap(() =>
                    this._documentController
                        .refreshOnlineUser(documentId)
                        .pipe(catchError(() => EMPTY))
                ),
                takeUntilDestroyed(_destroyRef)
            )
            .subscribe();
    }

    /**
     * Mantém o foco da seção atual enquanto a tela de edição estiver ativa
     */
    private _initializeSessionFocusRefresh(documentId: string, _destroyRef: DestroyRef): void {
        interval(10000)
            .pipe(
                exhaustMap(() => {
                    const sessionId = this._editDataService.getCurrentSessionId();
                    if (!sessionId || this._editDataService.currentSessionIsDisabled()) {
                        return EMPTY;
                    }

                    return this._documentSessionController
                        .refreshFocusInSection(documentId, sessionId)
                        .pipe(catchError(() => EMPTY));
                }),
                takeUntilDestroyed(_destroyRef)
            )
            .subscribe();
    }

    /**
     * Inicializa o pusher
     */
    private initializePusher(documentId: string, _destroyRef: DestroyRef): void {
        const channelName = `DOCUMENT-${documentId}`;

        // Ressincroniza o documento após reconexão do Pusher: eventos emitidos
        // durante a queda foram perdidos, então rebusca o estado no servidor
        this._initializeReconnectResync(_destroyRef);

        // Inicializa os eventos [Document]
        this._docOnlineUserEvent(channelName, _destroyRef);
        this._docTitleUpdatedEvent(channelName, _destroyRef);
        this._docContractUpdatedEvent(channelName, _destroyRef);
        this._docFooterUpdatedEvent(channelName, _destroyRef);
        this._docHeaderUpdatedEvent(channelName, _destroyRef);
        this._docCoverUpdatedEvent(channelName, _destroyRef);
        this._docCoverTemplateChangedEvent(channelName, _destroyRef);

        // Inicializa os eventos [Sessions]
        this._sessionCreatedEvent(channelName, _destroyRef);
        this._sessionsDeletedEvent(channelName, _destroyRef);
        this._sessionNameUpdatedEvent(channelName, _destroyRef);
        this._sessionSetFocusUpdatedEvent(channelName, _destroyRef);
        this._sessionRemoveFocusUpdatedEvent(channelName, _destroyRef);
        this._sessionsReorderedEvent(channelName, _destroyRef);

        // Inicializa os eventos [Checklist / Responsibles]
        this._docChecklistModeUpdatedEvent(channelName, _destroyRef);
        this._sessionChecklistUpdatedEvent(channelName, _destroyRef);
        this._sessionFolderChecklistUpdatedEvent(channelName, _destroyRef);
        this._docChecklistResetEvent(channelName, _destroyRef);
        this._docResponsiblesUpdatedEvent(channelName, _destroyRef);
        this._sessionDescriptionUpdatedEvent(channelName, _destroyRef);

        // Inicializa os eventos [Fields]
        this._fieldsCreatedEvent(channelName, _destroyRef);
        this._fieldUpdatedEvent(channelName, _destroyRef);
        this._fieldsDeletedEvent(channelName, _destroyRef);
        this._fieldsMovedEvent(channelName, _destroyRef);
        this._fieldRestoredEvent(channelName, _destroyRef);
        this._fieldsReorderedEvent(channelName, _destroyRef);
        this._sessionFieldsReloadEvent(channelName, _destroyRef);
        this._docGroupImageGridAppliedEvent(channelName, _destroyRef);

        // Inicializa os eventos [Editar com IA]. As alterações em si chegam pelos eventos
        // de campo/seção acima; estes só contam o andamento do pedido e o que o usuário
        // decidiu depois dele.
        this._aiEditStatusEvent(channelName, _destroyRef);
        this._aiEditConflictEvent(channelName, _destroyRef);
        this._aiEditChangeEvent(channelName, _destroyRef);
    }

    /**
     * Rebusca o documento no servidor sempre que a conexão do Pusher é
     * restabelecida, recuperando os eventos perdidos durante a queda.
     * `exhaustMap` evita requisições sobrepostas em reconexões em sequência.
     */
    private _initializeReconnectResync(_destroyRef: DestroyRef): void {
        this._pusherService
            .onReconnected()
            .pipe(
                exhaustMap(() => {
                    // Acesso seguro: getDocumentId() lança erro se o documento for nulo
                    const documentId = this._editDataService.document()?._id;
                    if (!documentId) return EMPTY;

                    return this._documentController
                        .getById(documentId)
                        .pipe(catchError(() => EMPTY));
                }),
                takeUntilDestroyed(_destroyRef)
            )
            .subscribe(document => this._editDataService.setDocument(document));
    }

    //#region // * [Pusher - Document]

    /**
     * Canal para alertar quando o título do documento for alterado
     * @param channelName Nome do canal
     * @param _destroyRef DestroyRef
     */
    private _docTitleUpdatedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'doc-title-updated')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (title: string) => this._editDataService.documentTitleUpdated(title),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.titleUpdateError')
                    ),
            });
    }

    /**
     * Canal para alertar quando o footer do documento for alterado
     * @param channelName Nome do canal
     * @param _destroyRef DestroyRef
     */
    private _docFooterUpdatedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'footer-updated')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (footer: DocumentGenericField[]) =>
                    this._editDataService.documentFooterUpdated(footer),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.footerUpdateError')
                    ),
            });
    }

    /**
     * Canal para alertar quando o header do documento for alterado
     * @param channelName Nome do canal
     * @param _destroyRef DestroyRef
     */
    private _docHeaderUpdatedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'header-updated')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (header: DocumentGenericField[]) =>
                    this._editDataService.documentHeaderUpdated(header),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.headerUpdateError')
                    ),
            });
    }

    /**
     * Canais para alertar quando a capa do documento for alterada (dados ou template).
     * @param channelName Nome do canal
     * @param _destroyRef DestroyRef
     */
    private _docCoverUpdatedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'cover-updated')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (cover: DocumentCoverPartial) =>
                    this._editDataService.documentCoverUpdated(cover),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.coverUpdateError')
                    ),
            });
    }

    private _docCoverTemplateChangedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'doc-cover-template-changed')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (cover: DocumentCoverPartial) =>
                    this._editDataService.documentCoverUpdated(cover),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.coverUpdateError')
                    ),
            });
    }

    /**
     * Canal para alertar quando o usuário online do documento for alterado
     * @param channelName Nome do canal
     * @param _destroyRef DestroyRef
     */
    private _docOnlineUserEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'doc-online-user')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: { user: UserEntity; isOnline: boolean }) =>
                    this._editDataService.documentOnlineUserUpdated(resp.user, resp.isOnline),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.onlineUserError')
                    ),
            });
    }

    /**
     * Canal para alertar quando o contrato do documento forem alterados
     * @param channelName Nome do canal
     * @param _destroyRef DestroyRef
     */
    private _docContractUpdatedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'doc-contract-changed')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: { status: DocumentStatusEnum; signedBy: SignedByPartial[] }) =>
                    this._editDataService.documentContractUpdated(resp.status, resp.signedBy),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.contractError')
                    ),
            });
    }

    //#endregion

    //#region // * [Pusher - Sessions]

    /**
     * Canal para alertar quando a seção for criada
     */
    private _sessionCreatedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'session-created')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: { createdBy: string; session: DocumentSessionPartial }) =>
                    this._editDataService.sessionCreated(resp.session, resp.createdBy),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.sessionCreatedError')
                    ),
            });
    }

    /**
     * Canal para alertar quando o nome da seção for alterado
     */
    private _sessionNameUpdatedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'session-name-updated')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: { name: string; sessionId: string }) =>
                    this._editDataService.sessionNameUpdated(resp.name, resp.sessionId),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.sessionNameError')
                    ),
            });
    }

    /**
     * Canal para alertar quando o foco da seção for definido
     */
    private _sessionSetFocusUpdatedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'session-set-focused')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: { sessionId: string; focused: DocumentSessionFocusedPartial }) =>
                    this._editDataService.sessionSetFocusUpdated(resp.sessionId, resp.focused),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.sessionFocusSetError')
                    ),
            });
    }

    /**
     * Canal para alertar quando o foco da seção for removido
     */
    private _sessionRemoveFocusUpdatedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'session-remove-focused')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: { sessionId: string }) =>
                    this._editDataService.sessionRemoveFocusUpdated(resp.sessionId),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.sessionFocusRemoveError')
                    ),
            });
    }

    /**
     * Canal para alertar quando sessoes forem deletadas
     */
    private _sessionsDeletedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'sessions-deleted')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (sessionsListIds: string[]) =>
                    this._editDataService.sessionsDeleted(sessionsListIds),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.sessionsDeletedError')
                    ),
            });
    }

    /**
     * Canal para alertar quando as sessões forem reordenadas
     */
    private _sessionsReorderedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'sessions-reordered')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (sessionsIds: string[]) =>
                    this._editDataService.sessionReordered(sessionsIds),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.sessionsReorderError')
                    ),
            });
    }

    // #endregion

    //#region // * [Pusher - Checklist / Responsibles]

    /** Canal: modo checklist ligado/desligado */
    private _docChecklistModeUpdatedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'doc-checklist-mode-updated')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: { checklistMode: boolean }) =>
                    this._editDataService.documentChecklistModeUpdated(resp.checklistMode),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.checklistModeError')
                    ),
            });
    }

    /** Canal: checklist de uma seção atualizado (concluída/não concluída) */
    private _sessionChecklistUpdatedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'session-checklist-updated')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: { sessionId: string; checklist: DocumentSessionChecklistPartial }) =>
                    this._editDataService.sessionChecklistUpdated(resp.sessionId, resp.checklist),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.sessionChecklistError')
                    ),
            });
    }

    /** Canal: checklist de uma PASTA atualizado (+ check derivado da seção) */
    private _sessionFolderChecklistUpdatedEvent(
        channelName: string,
        _destroyRef: DestroyRef
    ): void {
        this._pusherService
            .subscribe(channelName, 'session-folder-checklist-updated')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: {
                    sessionId: string;
                    itemId: string;
                    checklist: DocumentSessionChecklistPartial;
                    sessionChecklist: DocumentSessionChecklistPartial;
                }) =>
                    this._editDataService.folderChecklistUpdated(
                        resp.sessionId,
                        resp.itemId,
                        resp.checklist,
                        resp.sessionChecklist
                    ),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.sessionChecklistError')
                    ),
            });
    }

    /** Canal: checklist resetado (todas as seções desmarcadas) */
    private _docChecklistResetEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'doc-checklist-reset')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: () => this._editDataService.documentChecklistReset(),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.checklistResetError')
                    ),
            });
    }

    /** Canal: grid de imagens aplicado a todos os grupos do documento */
    private _docGroupImageGridAppliedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'doc-group-image-grid-applied')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (data: { groupImageGrid: number }) =>
                    this._editDataService.documentGroupImageGridApplied(data?.groupImageGrid),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.gridAppliedError')
                    ),
            });
    }

    /** Canal: responsáveis do documento atualizados */
    private _docResponsiblesUpdatedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'doc-responsibles-updated')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: { responsibles: DocumentResponsiblePartial[] }) =>
                    this._editDataService.documentResponsiblesUpdated(resp.responsibles),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.responsiblesError')
                    ),
            });
    }

    /** Canal: descrição de uma seção atualizada */
    private _sessionDescriptionUpdatedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'session-description-updated')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: { sessionId: string; description: string }) =>
                    this._editDataService.sessionDescriptionUpdated(
                        resp.description,
                        resp.sessionId
                    ),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.sessionDescriptionError')
                    ),
            });
    }

    // #endregion

    //#region // * [Pusher - Editar com IA]

    /**
     * Andamento de um pedido do modo "Editar com IA" (planning → applying → done/error).
     * Ver docs/EDITOR-CHAT-IA.md, seção 3.
     */
    private _aiEditStatusEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'ai-edit-status')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (event: AiEditStatusEvent) => this._aiEditStore.onStatus(event),
                error: error => console.error(error),
            });
    }

    /**
     * Uma alteração da IA esbarrou numa edição humana (409 no update-text). A IA nunca
     * sobrescreve: a alteração fica em conflito e quem decide é o usuário.
     */
    private _aiEditConflictEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'ai-edit-conflict')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (event: AiEditConflictEvent) => this._aiEditStore.onConflict(event),
                error: error => console.error(error),
            });
    }

    /**
     * Uma alteração mudou depois de o pedido terminar (conflito decidido, falha retentada),
     * aqui ou em outro aparelho do usuário: o cartão dela na conversa acompanha.
     */
    private _aiEditChangeEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'ai-edit-change')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (event: AiEditChangeEvent) => this._aiEditStore.onChangeUpdated(event),
                error: error => console.error(error),
            });
    }

    //#endregion

    //#region // * [Pusher - Fields]

    /**
     * Canal para alertar quando o campo for alterado
     */
    private _fieldsCreatedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'fields-created')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: {
                    fields: { value: DocumentItemPartial; index: number }[];
                    sessionId: string;
                    sessionSize: number;
                }) =>
                    this._editDataService.fieldsCreated(
                        resp.fields,
                        resp.sessionSize,
                        resp.sessionId
                    ),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.fieldsCreatedError')
                    ),
            });
    }

    /**
     * Canal para alertar quando o campo for alterado
     */
    private _fieldUpdatedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'field-updated')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: {
                    field: DocumentItemPartial;
                    sessionId: string;
                    sessionSize: number;
                }) => {
                    this._editDataService.fieldUpdated(
                        resp.field,
                        resp.sessionId,
                        resp.sessionSize
                    );

                    // Campo em janela de desfazer (fora da lista local): atualiza
                    // o snapshot para o Desfazer devolver a edição do outro
                    // usuário, não o texto velho.
                    this._fieldRemovalService.applyRtUpdateToPendingSnapshot(resp.field);
                },
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.fieldUpdateError')
                    ),
            });
    }

    /**
     * Canal de fallback para campos de mídia grandes.
     *
     * Quando o payload cheio de um evento de campo (created/updated/restored)
     * excede o limite de tamanho do Pusher, o servidor dispara este evento
     * "magro" com apenas o `sessionId`. Rebuscamos o documento e recarregamos
     * somente os campos da seção afetada, preservando seção atual, navegação de
     * pasta e seleção. `switchMap` garante que apenas o refetch mais recente
     * prevaleça em caso de eventos em sequência.
     */
    private _sessionFieldsReloadEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'session-fields-reload')
            .pipe(
                switchMap((resp: { sessionId: string }) => {
                    const documentId = this._editDataService.document()?._id;
                    if (!documentId || !resp?.sessionId) return EMPTY;

                    return this._documentController.getById(documentId).pipe(
                        tap(document =>
                            // Campos em janela de desfazer (removidos só
                            // localmente) ficam de fora: o reload não pode
                            // ressuscitá-los antes do commit.
                            this._editDataService.reloadSessionFields(
                                resp.sessionId,
                                document,
                                this._fieldRemovalService.pendingFieldIds
                            )
                        ),
                        catchError(error => {
                            this._utilService.handleError(
                                error,
                                this._t('document.edit.service.fieldUpdateError')
                            );
                            return EMPTY;
                        })
                    );
                }),
                takeUntilDestroyed(_destroyRef)
            )
            .subscribe();
    }

    /**
     * Canal para alertar quando os campos forem deletados
     */
    private _fieldsDeletedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'fields-deleted')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: { fieldsIds: string[]; sessionId: string; sessionSize: number }) =>
                    this._editDataService.fieldsDeleted(
                        resp.fieldsIds,
                        resp.sessionId,
                        resp.sessionSize
                    ),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.fieldsDeletedError')
                    ),
            });
    }

    /**
     * Canal para alertar quando os campos forem movidos
     */
    private _fieldsMovedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'fields-moved')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: {
                    currentSessionId: string;
                    targetSessionId: string;
                    fieldIds: string[];
                    targetFolderId?: string;
                }) =>
                    this._editDataService.fieldsMoved(
                        resp.currentSessionId,
                        resp.targetSessionId,
                        resp.fieldIds,
                        resp.targetFolderId
                    ),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.fieldsMovedError')
                    ),
            });
    }

    /**
     * Canal para alertar quando um campo for restaurado
     */
    private _fieldRestoredEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'field-restored')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: { session: DocumentSessionPartial; field: DocumentItemPartial }) =>
                    this._editDataService.fieldRestored(resp.session, resp.field),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.fieldRestoreError')
                    ),
            });
    }

    private _fieldsReorderedEvent(channelName: string, _destroyRef: DestroyRef): void {
        this._pusherService
            .subscribe(channelName, 'fields-reordered')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: { sessionId: string; fieldsIds: string[] }) =>
                    this._editDataService.fieldsReordered(resp.sessionId, resp.fieldsIds),
                error: error =>
                    this._utilService.handleError(
                        error,
                        this._t('document.edit.service.fieldsReorderError')
                    ),
            });
    }
    // #endregion
}
